<!--
  订单列表公共壳

  2 个菜单共用：
  - 「全部订单」：tabs = false，不传任何负责人口径 ⇒ 后端 sceneType = null = 不做数据权限过滤
  - 「我的订单」：tabs = true，页内 4 个 tab 切换口径（列清单也随口径变，与原型一致）

  4 种口径（后端同一个 /crm/order/page 接口）：
  - mine          sceneType = 1      → 我负责的
  - sub           sceneType = 3      → 我下属负责的
  - assignedToMe  assignedToMe=true  → 被分配人 = 当前用户（别人分配给我的）
  - assignedByMe  assignedByMe=true  → 分配人   = 当前用户（我分配给别人的）

  ⚠️ 需要按负责人过滤的口径，参数的**初值必须与 activeTab 一致**。若留 undefined，
  qs.stringify 会直接丢弃该参数，后端 sceneType = null 即「不做数据权限过滤」，
  会把别人的订单也查出来（越权）。故初始化时立刻 applyTabScene(activeTab)。
-->
<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="关键字" prop="keyword">
        <el-input
          v-model="queryParams.keyword"
          class="!w-280px"
          clearable
          placeholder="单号/项目名称/服务/甲方联系人/联系手机"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="省" prop="province">
        <el-select
          v-model="queryParams.province"
          class="!w-140px"
          clearable
          filterable
          placeholder="请选择省"
          @change="handleProvinceChange"
        >
          <el-option
            v-for="item in provinceOptions"
            :key="item.id"
            :label="item.name"
            :value="item.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="市" prop="city">
        <el-select
          v-model="queryParams.city"
          class="!w-140px"
          clearable
          filterable
          placeholder="请选择市"
        >
          <el-option
            v-for="item in cityOptions"
            :key="item.id"
            :label="item.name"
            :value="item.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="创建时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          :default-time="defaultTime"
          class="!w-340px"
          end-placeholder="结束日期"
          range-separator="-"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item label="阶段" prop="stage">
        <el-select v-model="queryParams.stage" class="!w-140px" clearable placeholder="请选择阶段">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_ORDER_STAGE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="客户种类" prop="customerCategory">
        <el-select
          v-model="queryParams.customerCategory"
          class="!w-150px"
          clearable
          placeholder="请选择客户种类"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_CUSTOMER_CATEGORY)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目经理" prop="projectManagerId">
        <el-select
          v-model="queryParams.projectManagerId"
          class="!w-160px"
          clearable
          filterable
          placeholder="请选择项目经理"
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="商务" prop="salesUserId">
        <el-select
          v-model="queryParams.salesUserId"
          class="!w-160px"
          clearable
          filterable
          placeholder="请选择商务"
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置筛选
        </el-button>
        <el-button
          :type="queryParams.starred ? 'warning' : undefined"
          @click="handleToggleStarredFilter"
        >
          <Icon class="mr-5px" icon="ep:star" />
          {{ queryParams.starred ? '取消筛选' : '标星订单' }}
        </el-button>
        <el-button v-hasPermi="['crm:order:create']" type="primary" @click="openForm('create')">
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <!-- 口径页签：仅「我的订单」显示，切换时口径与列清单一起换 -->
    <el-tabs v-if="tabs" v-model="activeTab" @tab-click="handleTabClick">
      <el-tab-pane
        v-for="(def, key) in TAB_DEFS"
        :key="key"
        :label="def.label"
        :name="String(key)"
      />
    </el-tabs>

    <div class="crm-order-toolbar">
      <el-button
        v-hasPermi="['crm:order:export']"
        :loading="exportLoading"
        plain
        type="success"
        @click="handleExport"
      >
        <Icon class="mr-5px" icon="ep:download" />
        导出
      </el-button>
      <el-select
        v-model="assigneeUserId"
        class="!w-180px"
        clearable
        filterable
        placeholder="选择分配人员"
      >
        <el-option
          v-for="item in userOptions"
          :key="item.id"
          :label="item.nickname"
          :value="item.id"
        />
      </el-select>
      <el-button
        v-hasPermi="['crm:order:assign']"
        :disabled="!assigneeUserId"
        plain
        type="primary"
        @click="handleAssign"
      >
        <Icon class="mr-5px" icon="ep:share" />
        一键分配
      </el-button>
      <el-button :disabled="selectedIds.length === 0" @click="handleStarred(true)">标星</el-button>
      <el-button :disabled="selectedIds.length === 0" @click="handleStarred(false)">
        取消标星
      </el-button>
      <el-button :disabled="selectedIds.length === 0" @click="handleTopped(false)">
        取消置顶
      </el-button>
      <span v-if="selectedIds.length > 0" class="crm-order-selected">
        已选 {{ selectedIds.length }} 条
      </span>
    </div>

    <el-table
      v-loading="loading"
      :data="list"
      :show-overflow-tooltip="true"
      :stripe="true"
      @selection-change="handleSelectionChange"
      @sort-change="handleSortChange"
    >
      <el-table-column fixed="left" type="selection" width="50" />
      <el-table-column
        v-for="col in visibleColumns"
        :key="col.key"
        align="center"
        :label="col.label"
        :prop="col.key"
        :show-overflow-tooltip="col.tooltip !== false"
        :sortable="col.sortable ? 'custom' : false"
        :width="col.width"
      >
        <template #default="scope">
          <el-link
            v-if="col.link === 'customer' && scope.row.customerId"
            :underline="false"
            type="primary"
            @click="openCustomerDetail(scope.row.customerId)"
          >
            {{ scope.row.customerName }}
          </el-link>
          <dict-tag v-else-if="col.dictType" :type="col.dictType" :value="scope.row[col.key]" />
          <!-- ★ 日期列：本组件每列都渲染了 #default 插槽，插槽会**覆盖** :formatter ⇒ 必须在这里格式化 -->
          <span v-else-if="col.date">{{ formatDate(scope.row[col.key]) }}</span>
          <template v-else>{{ scope.row[col.key] }}</template>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        fixed="right"
        label="操作"
        :show-overflow-tooltip="false"
        width="170"
      >
        <template #default="scope">
          <div class="crm-order-action">
            <el-button link type="primary" @click="openDetail(scope.row.id)">详情</el-button>
            <el-button
              v-hasPermi="['crm:order:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:order:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加 / 修改 -->
  <OrderForm ref="formRef" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { formatDate } from '@/utils/formatTime'
import download from '@/utils/download'
import { OrderApi, OrderVO } from '@/api/crm/order'
import * as AreaApi from '@/api/system/area'
import * as UserApi from '@/api/system/user'
import { TabsPaneContext } from 'element-plus'
import OrderForm from './OrderForm.vue'

/** 列定义：key 与后端 RespVO 字段同名，直接用于 prop */
interface OrderColumnDef {
  key: string
  label: string
  width: number
  /** 是否支持自定义排序（原型表头带 ⇅ 的列） */
  sortable?: boolean
  /** 字典类型 */
  dictType?: string
  /** 特殊渲染：customer 表示渲染为可跳转的客户链接 */
  link?: string
  /** 是否展示溢出提示（长文本列关闭，避免 tooltip 抢焦点） */
  tooltip?: boolean
  /** 是否为日期时间列（本组件每列都渲染 #default 插槽 ⇒ 须在插槽内用 formatDate 格式化） */
  date?: boolean
}

const COLUMN_DEFS: Record<string, OrderColumnDef> = {
  customerName: {
    key: 'customerName',
    label: '客户名称',
    width: 200,
    sortable: true,
    link: 'customer'
  },
  no: { key: 'no', label: '订单编号', width: 180, sortable: true },
  projectName: { key: 'projectName', label: '项目名称', width: 220 },
  port: { key: 'port', label: '端口', width: 140 },
  productName: { key: 'productName', label: '产品', width: 160 },
  service: { key: 'service', label: '服务', width: 120 },
  contactName: { key: 'contactName', label: '甲方联系人', width: 120 },
  contactMobile: { key: 'contactMobile', label: '联系手机', width: 140 },
  contactWechat: { key: 'contactWechat', label: '微信', width: 140 },
  projectManagerName: { key: 'projectManagerName', label: '项目经理', width: 120 },
  testOwnerName: { key: 'testOwnerName', label: '测试负责人', width: 120 },
  creatorName: { key: 'creatorName', label: '申请人', width: 110 },
  // ★ 2026-09-22 按用户反馈补的 4 列：crm_order 的既有字段，CrmOrderRespVO 已返回、
  //   字典（crm_order_progress / crm_order_status）也已配好 ⇒ 仅前端加列，零后端/零 SQL 改动。
  deliveryDate: { key: 'deliveryDate', label: '交付日期', width: 180, date: true },
  progress: { key: 'progress', label: '进度', width: 110, dictType: DICT_TYPE.CRM_ORDER_PROGRESS },
  status: { key: 'status', label: '状态', width: 110, dictType: DICT_TYPE.CRM_ORDER_STATUS },
  progressDesc: { key: 'progressDesc', label: '进度说明', width: 180 }
}

/**
 * 「全部订单」的列清单
 *
 * 前 7 列 = 原型 `原型图/订单/订单管理/全部订单-列表页.png` 原样
 *   （客户名称⇅ / 项目名称 / 端口 / 甲方联系人 / 项目经理 / 测试负责人 / 申请人）
 * 后 4 列 = 2026-09-22 按用户反馈追加的「追加段」
 *   （交付日期 / 进度 / 状态 / 进度说明）—— 与 fe §3.5「原型列段在前 + 追加段在后」同口径
 */
const ALL_COLUMNS = [
  'customerName',
  'projectName',
  'port',
  'contactName',
  'projectManagerName',
  'testOwnerName',
  'creatorName',
  'deliveryDate',
  'progress',
  'status',
  'progressDesc'
]

type OrderTabKey = 'mine' | 'sub' | 'assignedToMe' | 'assignedByMe'

/**
 * 「我的订单」页内 4 个 tab：口径 + 列清单
 * 列清单按原型逐页对齐（我负责的 / 我下属负责的 / 我分配给别人的 三页同列）
 */
const TAB_DEFS: Record<OrderTabKey, { label: string; columns: string[] }> = {
  mine: {
    label: '我负责的',
    columns: [
      'projectName',
      'productName',
      'service',
      'contactName',
      'contactMobile',
      'contactWechat',
      'projectManagerName',
      'creatorName'
    ]
  },
  sub: {
    label: '我下属负责的',
    columns: [
      'projectName',
      'productName',
      'service',
      'contactName',
      'contactMobile',
      'contactWechat',
      'projectManagerName',
      'creatorName'
    ]
  },
  assignedToMe: {
    label: '别人分配给我的',
    columns: [
      'customerName',
      'productName',
      'service',
      'contactName',
      'contactMobile',
      'contactWechat',
      'no',
      'projectManagerName',
      'testOwnerName'
    ]
  },
  assignedByMe: {
    label: '我分配给别人的',
    columns: [
      'projectName',
      'productName',
      'service',
      'contactName',
      'contactMobile',
      'contactWechat',
      'projectManagerName',
      'creatorName'
    ]
  }
}

const props = withDefaults(
  defineProps<{
    /** 是否「我的订单」页：显示 4 个口径页签，列清单随页签切换 */
    tabs?: boolean
  }>(),
  {
    tabs: false
  }
)

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const { push } = useRouter()

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总记录数
const list = ref<OrderVO[]>([]) // 列表的数据
const userOptions = ref<UserApi.UserVO[]>([]) // 用户下拉
const exportLoading = ref(false) // 导出的加载中
const selectedIds = ref<number[]>([]) // 表格勾选的编号
const assigneeUserId = ref<number>() // 「选择分配人员」的下拉值
const areaTree = ref<any[]>([]) // 地区树（一级 = 省）
const cityOptions = ref<any[]>([]) // 市下拉
const activeTab = ref<OrderTabKey>('mine') // 「我的订单」当前口径页签
/** 日期范围默认带时分秒 00:00:00 ~ 23:59:59 */
const defaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]

/**
 * 把 tab 口径写入查询参数（三个参数互斥，先全清再赋值）
 * 全部订单页不传任何口径 ⇒ 后端不做数据权限过滤
 */
const applyTabScene = (tab: OrderTabKey) => {
  queryParams.sceneType = undefined
  queryParams.assignedToMe = undefined
  queryParams.assignedByMe = undefined
  if (tab === 'mine') {
    queryParams.sceneType = 1
  } else if (tab === 'sub') {
    queryParams.sceneType = 3
  } else if (tab === 'assignedToMe') {
    queryParams.assignedToMe = true
  } else if (tab === 'assignedByMe') {
    queryParams.assignedByMe = true
  }
}

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 15,
  keyword: undefined,
  province: undefined,
  city: undefined,
  createTime: [],
  stage: undefined,
  customerCategory: undefined,
  projectManagerId: undefined,
  salesUserId: undefined,
  starred: undefined,
  sortField: undefined,
  sortOrder: undefined,
  // ★ 口径参数：初值必须与 activeTab 保持一致，见下方 applyTabScene 调用与文件头注释。
  sceneType: undefined,
  assignedToMe: undefined,
  assignedByMe: undefined
})
// ★ 「我的订单」初始口径 = 我负责的（sceneType=1）；「全部订单」不设置任何口径 = 全量
if (props.tabs) {
  applyTabScene(activeTab.value)
}
const queryFormRef = ref() // 搜索的表单
const provinceOptions = computed(() => areaTree.value) // 省下拉
const visibleColumns = computed(() =>
  (props.tabs ? TAB_DEFS[activeTab.value].columns : ALL_COLUMNS)
    .map((key) => COLUMN_DEFS[key])
    .filter((col) => !!col)
)

/** 页签切换：口径与列清单一起换，并回到第 1 页 */
const handleTabClick = (tab: TabsPaneContext) => {
  const name = tab.paneName as OrderTabKey
  if (!TAB_DEFS[name]) {
    return
  }
  activeTab.value = name
  applyTabScene(name)
  handleQuery()
}

/** 省变化：联动市下拉，并清空已选市 */
const handleProvinceChange = (provinceName: string) => {
  queryParams.city = undefined
  const province = provinceOptions.value.find((item) => item.name === provinceName)
  cityOptions.value = province && province.children ? province.children : []
}

/** 表格勾选变化 */
const handleSelectionChange = (rows: OrderVO[]) => {
  selectedIds.value = rows.map((row) => row.id as number)
}

/** 表头排序变化（客户名称 / 订单编号） */
const handleSortChange = (payload: any) => {
  queryParams.sortField = payload.order ? payload.prop : undefined
  queryParams.sortOrder = payload.order ? payload.order : undefined
  handleQuery()
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await OrderApi.getOrderPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作：只清筛选条件，**不动口径参数**（口径由页签决定） */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.keyword = undefined
  queryParams.province = undefined
  queryParams.city = undefined
  queryParams.createTime = []
  queryParams.stage = undefined
  queryParams.customerCategory = undefined
  queryParams.projectManagerId = undefined
  queryParams.salesUserId = undefined
  queryParams.starred = undefined
  queryParams.sortField = undefined
  queryParams.sortOrder = undefined
  cityOptions.value = []
  handleQuery()
}

/** 标星 / 取消标星筛选 的快捷切换 */
const handleToggleStarredFilter = () => {
  queryParams.starred = queryParams.starred ? undefined : true
  handleQuery()
}

/** 添加 / 修改操作 */
const formRef = ref<InstanceType<typeof OrderForm>>()
const openForm = (type: string, id?: number) => {
  formRef.value?.open(type, id)
}

/** 详情：复用编辑弹窗，表单内只读展示 */
const openDetail = (id: number) => {
  formRef.value?.open('detail', id)
}

/** 批量分配 */
const handleAssign = async () => {
  if (selectedIds.value.length === 0) {
    message.warning('请先勾选需要分配的订单')
    return
  }
  try {
    await message.confirm('确认将选中的 ' + selectedIds.value.length + ' 条订单分配给该人员吗？')
    await OrderApi.assignOrders(selectedIds.value, assigneeUserId.value as number)
    message.success('分配成功')
    await getList()
  } catch {}
}

/** 批量标星 / 取消标星 */
const handleStarred = async (starred: boolean) => {
  try {
    await OrderApi.updateOrderStarred(selectedIds.value, starred)
    message.success(starred ? '标星成功' : '取消标星成功')
    await getList()
  } catch {}
}

/** 批量置顶 / 取消置顶 */
const handleTopped = async (topped: boolean) => {
  try {
    await OrderApi.updateOrderTopped(selectedIds.value, topped)
    message.success(topped ? '置顶成功' : '取消置顶成功')
    await getList()
  } catch {}
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await OrderApi.deleteOrder(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    await message.exportConfirm()
    exportLoading.value = true
    const data = await OrderApi.exportOrder(queryParams)
    download.excel(data, '订单.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 打开客户详情 */
const openCustomerDetail = (id: number) => {
  push({ name: 'CrmCustomerDetail', params: { id } })
}

/** 初始化 **/
onMounted(async () => {
  userOptions.value = await UserApi.getSimpleUserList()
  areaTree.value = await AreaApi.getAreaTree()
  await getList()
})
</script>

<style lang="scss" scoped>
.crm-order-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}

.crm-order-selected {
  color: var(--el-text-color-secondary);
  font-size: 13px;
}

.crm-order-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
